/**
 * Msg 571 · Phase I.2 — Admin template editor.
 *
 * Three pages, one component file to keep imports tight and avoid the
 * visual-edits babel plugin's known deep-recursion issue on
 * frontend/atlas paths:
 *
 *   • <AtlasTemplatesListPage>       — /atlas/admin/templates
 *   • <AtlasTemplateEditorPage>      — /atlas/admin/templates/:key   (also :key = "__new__")
 *   • <AtlasTemplateParentMapPage>   — /atlas/admin/template-parent-map
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  Plus, Trash2, Save, ChevronDown, ChevronRight, Loader2,
  ArrowLeft, AlertTriangle, Folder as FolderIcon, X as CloseIcon,
  Layers, Link as LinkIcon, ArrowUpRight, Pencil,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";


// Msg 574 · UX-Cleanup — admin-facing attribute types with plain-English
// display labels. The backing value stays the same (`string`, `bool`,
// etc.) so backend contracts are untouched.
const ATTR_TYPES = ["string", "number", "bool", "list", "link", "date"];
const ATTR_TYPE_LABELS = {
  string: "Text",
  number: "Number",
  bool:   "Yes / No",
  list:   "List of items",
  link:   "Link (URL)",
  date:   "Date",
};

// Msg 574 — slugify a Label into a snake_case attribute key. Applied
// only when the user hasn't touched the key field directly (advanced
// mode) so we don't overwrite intentional custom keys.
function _labelToKey(label) {
  return (label || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}
const ENTITY_TYPES = [
  "other", "destination", "cruise_line", "cruise_line_group",
  "cruise_ship", "resort", "hotel", "port", "region",
];


// Msg 575 — When these admin components are mounted UNDER `/atlas/topics`
// (the consolidated Topics tab), internal navigation uses search-params so
// the segmented-control view stays in sync. When mounted at legacy
// `/atlas/admin/...` URLs, they fall back to react-router path navigation.
function useTemplatesAdminNav() {
  const nav = useNavigate();
  const loc = useLocation();
  const [sp, setSp] = useSearchParams();
  const embedded = loc.pathname.startsWith("/atlas/topics");
  return {
    embedded,
    goList: () => {
      if (embedded) {
        const next = new URLSearchParams(sp);
        next.set("view", "templates");
        next.delete("open");
        setSp(next, { replace: false });
      } else {
        nav("/atlas/admin/templates");
      }
    },
    goEdit: (key, opts = {}) => {
      if (embedded) {
        const next = new URLSearchParams(sp);
        next.set("view", "templates");
        next.set("open", key);
        setSp(next, { replace: !!opts.replace });
      } else {
        nav(`/atlas/admin/templates/${key}`, { replace: !!opts.replace });
      }
    },
    goParentMap: () => {
      if (embedded) {
        const next = new URLSearchParams(sp);
        next.set("view", "parent-map");
        next.delete("open");
        setSp(next);
      } else {
        nav("/atlas/admin/template-parent-map");
      }
    },
  };
}


// ═════════════════════════════════════════════════════════════
// LIST PAGE — grid of template cards + heat-map header.
// ═════════════════════════════════════════════════════════════
export function AtlasTemplatesListPage() {
  const admNav = useTemplatesAdminNav();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/atlas/admin/templates")
      .then((r) => setRows(r.data?.templates || []))
      .catch((ex) => toast.error(formatApiErrorDetail(ex?.response?.data?.detail)
                                  || "Failed to load templates"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const stats = useMemo(() => {
    const total = rows.length;
    const totalTopics = rows.reduce((n, r) => n + (r.used_by_count || 0), 0);
    const unused = rows.filter((r) => (r.used_by_count || 0) === 0).length;
    return { total, totalTopics, unused };
  }, [rows]);

  const onDelete = async (key, forceHint) => {
    if (!confirm(`Delete template "${key}"?${forceHint ? " Force-deleting will not remove topics using it." : ""}`)) return;
    setDeletingKey(key);
    try {
      await api.delete(`/atlas/admin/templates/${key}?force=${forceHint ? "true" : "false"}`);
      toast.success(`Template "${key}" deleted.`);
      load();
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = formatApiErrorDetail(ex?.response?.data?.detail);
      if (status === 409) {
        if (confirm(`${detail}\n\nForce-delete anyway?`)) return onDelete(key, true);
      } else {
        toast.error(detail || "Delete failed.");
      }
    } finally { setDeletingKey(null); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="atlas-templates-list">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="font-hand text-[11px] uppercase tracking-widest text-tb-navy/50">
            Atlas Admin
          </div>
          <h1 className="mt-1 text-tb-navy text-3xl md:text-4xl font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Topic Templates
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-xl">
            Templates define the folders and fields that auto-populate when
            advisors create new topics. They aren't topics themselves —
            think of them as blueprints. Editing "Individual cruise ship"
            means every Icon of the Seas or Wonder of the Seas created
            afterward gets the same folders and fields.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button"
            onClick={() => admNav.goParentMap()}
            data-testid="atlas-templates-open-parent-map"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-sm hover:bg-tb-navy/5">
            <LinkIcon className="w-4 h-4" /> Parent map
          </button>
          <button type="button"
            onClick={() => admNav.goEdit("__new__")}
            data-testid="atlas-templates-new"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
            <Plus className="w-4 h-4" /> New template
          </button>
        </div>
      </div>

      <div data-testid="atlas-templates-heatmap"
        className="mt-6 inline-flex items-center gap-2 h-9 px-4 rounded-full border border-tb-navy/15 bg-tb-navy/[0.03] font-hand text-[11px] text-tb-navy/80">
        <Layers className="w-3.5 h-3.5" />
        <b>{stats.total}</b> templates · <b>{stats.totalTopics}</b> topics using them
        {stats.unused > 0 && <> · <b>{stats.unused}</b> unused</>}
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
           data-testid="atlas-templates-grid">
        {loading && Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-40 rounded-2xl bg-tb-navy/5 animate-pulse" />
        ))}
        {!loading && rows.length === 0 && (
          <div className="col-span-full rounded-2xl border-2 border-dashed border-tb-navy/20 bg-tb-navy/[0.02] p-8 text-center">
            <p className="font-hand text-tb-navy/60 text-sm">
              No templates defined. Create your first one to enable auto-scaffolding.
            </p>
          </div>
        )}
        {!loading && rows.map((r) => (
          <TemplateCard key={r.key} row={r}
            onEdit={() => admNav.goEdit(r.key)}
            onDelete={() => onDelete(r.key, false)}
            busy={deletingKey === r.key}
          />
        ))}
      </div>
    </div>
  );
}


function TemplateCard({ row, onEdit, onDelete, busy }) {
  const attrCount = (row.attributes || []).length;
  const folderCount = (row.folders || []).length;
  const usedBy = row.used_by_count || 0;
  return (
    <div className="rounded-2xl border border-tb-navy/15 bg-white p-4 flex flex-col"
         data-testid={`atlas-template-card-${row.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-tb-navy text-base font-semibold truncate"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            {row.name || row.key}
          </h3>
          <code className="font-hand text-[10px] text-tb-navy/50">{row.key}</code>
        </div>
        <span className={`text-[10px] font-hand rounded-full px-2 py-0.5 ${
          usedBy > 0 ? "bg-tb-gold-bright/20 text-tb-navy ring-1 ring-tb-gold-bright/40"
                     : "bg-tb-navy/5 text-tb-navy/50"
        }`}
              data-testid={`atlas-template-used-by-${row.key}`}>
          {usedBy} in use
        </span>
      </div>
      {row.description && (
        <p className="mt-2 font-hand text-[11px] text-tb-navy/60 line-clamp-2">
          {row.description}
        </p>
      )}
      <div className="mt-3 flex items-center gap-3 font-hand text-[11px] text-tb-navy/70">
        <span>{attrCount} attribute{attrCount === 1 ? "" : "s"}</span>
        <span className="opacity-40">·</span>
        <span>{folderCount} folder{folderCount === 1 ? "" : "s"}</span>
      </div>
      <div className="mt-4 flex items-center gap-2">
        <button type="button" onClick={onEdit}
          data-testid={`atlas-template-edit-${row.key}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <button type="button" onClick={onDelete} disabled={busy}
          data-testid={`atlas-template-delete-${row.key}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-red-300 text-red-700 font-hand text-xs hover:bg-red-50 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Delete
        </button>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════
// EDITOR PAGE — form + nested folder tree.
// ═════════════════════════════════════════════════════════════
export function AtlasTemplateEditorPage() {
  const { key: routeParamKey } = useParams();
  const admNav = useTemplatesAdminNav();
  const [sp] = useSearchParams();
  // Msg 575 — when embedded, the target template key comes from
  // `?open=<key>` instead of the URL path.
  const routeKey = admNav.embedded ? (sp.get("open") || "__new__") : routeParamKey;
  const isNew = routeKey === "__new__";
  const [form, setForm] = useState({
    key: "", name: "", description: "", entity_type: "other",
    attributes: [], folders: [],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [usedByCount, setUsedByCount] = useState(0);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    api.get(`/atlas/admin/templates/${routeKey}`)
      .then((r) => {
        const d = r.data || {};
        setForm({
          key: d.key || "",
          name: d.name || "",
          description: d.description || "",
          entity_type: d.entity_type || "other",
          attributes: d.attributes || [],
          folders: d.folders || [],
        });
        setUsedByCount(d.used_by_count || 0);
      })
      .catch(() => toast.error("Failed to load template."))
      .finally(() => setLoading(false));
  }, [routeKey, isNew]);

  const [propModal, setPropModal] = useState(null);   // {diff, usedBy, tplKey, tplName} | null
  const [propBatch, setPropBatch] = useState(null);   // {batch_id, counts, template_key, applied_at} | null after apply

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, key: (form.key || "").trim().toLowerCase() };
      if (isNew) {
        await api.post("/atlas/admin/templates", body);
        toast.success(`Template "${body.key}" created.`);
        admNav.goEdit(body.key, { replace: true });
      } else {
        const r = await api.put(`/atlas/admin/templates/${routeKey}`, body);
        const diff = r.data?.diff || {};
        const used = r.data?.used_by_count || 0;
        setUsedByCount(used);
        const cascadeChanges = ["attributes_removed", "folders_added",
                                  "folders_removed", "folders_renamed"]
                                  .some((k) => (diff[k] || []).length > 0);
        // Msg 573 · I.4 — surface the propagation modal only when the
        // diff has cascade-worthy changes AND there are topics using
        // the template. Otherwise, silent success.
        if (cascadeChanges && used > 0) {
          setPropModal({ diff, usedBy: used, tplKey: routeKey, tplName: form.name || routeKey });
          toast.success("Template saved. Retro cascade available →");
        } else {
          toast.success("Template saved.");
        }
      }
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-8 font-hand text-tb-navy/60">Loading…</div>;
  }

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="atlas-template-editor">
      <div className="flex items-center gap-2 mb-4">
        <button type="button" onClick={() => admNav.goList()}
          data-testid="atlas-template-editor-back"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-tb-navy/70 hover:bg-tb-navy/5 font-hand text-xs">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to templates
        </button>
        {!isNew && (
          <span className="font-hand text-[10px] text-tb-navy/50">
            <b>{usedByCount}</b> topic{usedByCount === 1 ? "" : "s"} use this template
          </span>
        )}
      </div>

      <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold"
          style={{ fontFamily: '"Playfair Display", serif' }}>
        {isNew ? "New template" : `Edit "${form.name || form.key}"`}
      </h1>

      <BasicsSection form={form} setForm={setForm} isNew={isNew} />

      <AttributesTable
        attributes={form.attributes || []}
        onChange={(next) => setForm((f) => ({ ...f, attributes: next }))}
        testIdPrefix="tpl-root"
      />

      <FoldersEditor
        folders={form.folders || []}
        onChange={(next) => setForm((f) => ({ ...f, folders: next }))}
        depth={0}
      />

      <div className="mt-8 flex items-center justify-end gap-2 sticky bottom-4 z-10">
        <button type="button" onClick={() => admNav.goList()}
          data-testid="atlas-template-editor-cancel"
          className="h-10 px-5 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
          Cancel
        </button>
        <button type="button" onClick={save} disabled={saving}
          data-testid="atlas-template-editor-save"
          className="inline-flex items-center gap-1.5 h-10 px-6 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>

      {propModal && (
        <PropagationModal
          modal={propModal}
          onClose={() => setPropModal(null)}
          onApplied={(batch) => { setPropModal(null); setPropBatch(batch); }}
        />
      )}
      {propBatch && (
        <UndoBanner batch={propBatch} onUndone={() => setPropBatch(null)}
          onDismiss={() => setPropBatch(null)} />
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════
// Msg 573 · Phase I.4 — Propagation modal + Undo banner.
// ═════════════════════════════════════════════════════════════

function PropagationModal({ modal, onClose, onApplied }) {
  const { diff, usedBy, tplKey, tplName } = modal;
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const removed = diff.attributes_removed || [];
  const foldersAdded = diff.folders_added || [];
  const foldersRemoved = diff.folders_removed || [];
  const foldersRenamed = diff.folders_renamed || [];

  const loadPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(`/atlas/admin/templates/${tplKey}/propagate/preview`);
      setPreview(r.data);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Preview failed.");
    } finally { setPreviewing(false); }
  };

  const applyRetro = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/atlas/admin/templates/${tplKey}/propagate`,
        { diff, dry_run: false });
      const batch = r.data || {};
      if (!batch.batch_id) {
        toast("No cascade-worthy changes applied.");
        onClose(); return;
      }
      toast.success(`Applied to ${JSON.stringify(batch.counts)}.`);
      onApplied({ ...batch, template_key: tplKey, applied_at: Date.now() });
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Apply failed.");
    } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-propagation-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-5 pt-5 pb-3 border-b border-tb-navy/10">
          <h2 className="text-tb-navy text-lg font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Template updated — cascade to existing topics?
          </h2>
          <p className="mt-1 font-hand text-tb-navy/70 text-xs">
            Template "<b>{tplName}</b>" has {usedBy} topic{usedBy === 1 ? "" : "s"} using it.
            Apply this diff retroactively? Undo window: <b>24 h</b>.
          </p>
        </div>
        <div className="p-5 space-y-2 font-hand text-[12px] text-tb-navy/80"
             data-testid="atlas-propagation-diff-summary">
          {removed.length > 0 && (
            <div><b className="text-red-700">REMOVE</b> attributes:{" "}
              <code>{removed.join(", ")}</code></div>
          )}
          {foldersAdded.length > 0 && (
            <div><b className="text-emerald-700">ADD</b> folders:{" "}
              <code>{foldersAdded.join(", ")}</code></div>
          )}
          {foldersRemoved.length > 0 && (
            <div><b className="text-red-700">REMOVE</b> folders:{" "}
              <code>{foldersRemoved.join(", ")}</code></div>
          )}
          {foldersRenamed.length > 0 && (
            <div><b className="text-amber-700">RENAME</b> folders:{" "}
              <code>{foldersRenamed.map((r) => `${r.from} → ${r.to}`).join("; ")}</code></div>
          )}
          {preview && (
            <div className="mt-3 rounded-lg border border-tb-navy/15 bg-tb-navy/[0.03] p-2 max-h-32 overflow-y-auto"
                 data-testid="atlas-propagation-preview-list">
              <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
                Affected topics ({preview.affected_root_count})
              </div>
              <ul className="mt-1 space-y-0.5">
                {(preview.affected_roots || []).map((t) => (
                  <li key={t.slug} className="text-tb-navy/70">
                    <code>{t.slug}</code> — {t.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-tb-navy/10 flex items-center justify-end gap-2 flex-wrap">
          <button type="button" onClick={loadPreview} disabled={previewing}
            data-testid="atlas-propagation-preview-btn"
            className="h-9 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs">
            {previewing ? "Loading…" : "Preview affected topics"}
          </button>
          <button type="button" onClick={onClose}
            data-testid="atlas-propagation-skip"
            className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
            Skip — new topics only
          </button>
          <button type="button" onClick={applyRetro} disabled={applying}
            data-testid="atlas-propagation-apply"
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
            Apply retroactively
          </button>
        </div>
      </div>
    </div>
  );
}


function UndoBanner({ batch, onUndone, onDismiss }) {
  const [undoing, setUndoing] = useState(false);
  const undo = async () => {
    if (!confirm("Undo this propagation? Restores affected topics to their prior state.")) return;
    setUndoing(true);
    try {
      const r = await api.post(
        `/atlas/admin/templates/${batch.template_key}/propagate/${batch.batch_id}/undo`);
      toast.success(`Undone. Restored ${JSON.stringify(r.data?.restored || {})}.`);
      onUndone();
    } catch (ex) {
      const status = ex?.response?.status;
      const detail = formatApiErrorDetail(ex?.response?.data?.detail);
      if (status === 410) toast.error("Undo window expired.");
      else if (status === 409) toast.error("Already undone.");
      else toast.error(detail || "Undo failed.");
    } finally { setUndoing(false); }
  };
  return (
    <div className="fixed bottom-4 right-4 z-40 rounded-2xl bg-tb-navy text-white p-4 shadow-xl max-w-sm"
         data-testid="atlas-propagation-undo-banner">
      <div className="font-hand text-xs">
        <b>Propagation applied.</b> Undo available for <b>24 h</b>.
        <div className="mt-1 text-white/70 text-[10px]">
          Counts: {JSON.stringify(batch.counts || {})}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={undo} disabled={undoing}
          data-testid="atlas-propagation-undo-btn"
          className="h-8 px-3 rounded-full bg-tb-gold-bright text-tb-navy font-hand text-xs disabled:opacity-50">
          {undoing ? "Undoing…" : "Undo"}
        </button>
        <button type="button" onClick={onDismiss}
          data-testid="atlas-propagation-undo-dismiss"
          className="h-8 px-3 rounded-full border border-white/25 text-white font-hand text-xs">
          Dismiss
        </button>
      </div>
    </div>
  );
}


function BasicsSection({ form, setForm, isNew }) {
  return (
    <div className="mt-6 rounded-2xl border border-tb-navy/15 bg-white p-5"
         data-testid="atlas-template-basics">
      <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 mb-3">
        Basics
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
            Key {isNew ? "(lowercase snake_case)" : "(read-only)"}
          </span>
          <input value={form.key} readOnly={!isNew}
            onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
            data-testid="atlas-template-key"
            className={`mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm ${
              isNew ? "bg-white text-tb-navy" : "bg-tb-navy/5 text-tb-navy/60"
            }`} />
        </label>
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Name</span>
          <input value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="atlas-template-name"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
        </label>
        <label className="block sm:col-span-2">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Description</span>
          <textarea value={form.description} rows={2}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            data-testid="atlas-template-description"
            className="mt-1 w-full px-3 py-2 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
        </label>
        <label className="block">
          <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
            Default folder type
          </span>
          <select value={form.entity_type}
            onChange={(e) => setForm((f) => ({ ...f, entity_type: e.target.value }))}
            data-testid="atlas-template-entity-type"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}


// Attributes table — flat within its scope (root template OR any folder).
// Msg 574 · UX-Cleanup — default rows show only [Label | Type | Actions].
// A "Show advanced" toggle at the top reveals the Key and Examples
// columns for the rare case where an admin needs to hand-pick the
// storage key (backend contract) or seed example values. Keys are
// auto-derived from the label via `_labelToKey` when the user hasn't
// touched them directly.
function AttributesTable({ attributes, onChange, testIdPrefix }) {
  const [advanced, setAdvanced] = useState(false);
  // Track per-row whether the admin has hand-edited the key. If they
  // haven't, we auto-derive it from the label on every label change so
  // saves aren't blocked on an empty key field.
  const [dirtyKeys, setDirtyKeys] = useState(() => new Set());

  const setAt = (i, patch) => {
    const next = attributes.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const setLabel = (i, newLabel) => {
    const patch = { label: newLabel };
    if (!dirtyKeys.has(i)) {
      patch.key = _labelToKey(newLabel);
    }
    setAt(i, patch);
  };
  const setKey = (i, newKey) => {
    if (!dirtyKeys.has(i)) {
      setDirtyKeys((prev) => { const n = new Set(prev); n.add(i); return n; });
    }
    setAt(i, { key: newKey });
  };
  const add = () => onChange([...(attributes || []),
    { key: "", label: "", type: "string", examples: [] }]);
  const remove = (i) => {
    const next = attributes.slice();
    next.splice(i, 1);
    onChange(next);
    setDirtyKeys((prev) => {
      const n = new Set();
      for (const idx of prev) {
        if (idx < i) n.add(idx);
        else if (idx > i) n.add(idx - 1);
      }
      return n;
    });
  };

  const labelCls = advanced ? "col-span-4" : "col-span-8";
  const typeCls  = advanced ? "col-span-2" : "col-span-3";

  return (
    <div className="mt-4 rounded-2xl border border-tb-navy/15 bg-white p-4"
         data-testid={`${testIdPrefix}-attrs-panel`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
            Attributes
          </div>
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={advanced}
              onChange={(e) => setAdvanced(e.target.checked)}
              data-testid={`${testIdPrefix}-attrs-advanced-toggle`}
              className="accent-tb-navy w-3.5 h-3.5" />
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">
              Show advanced
            </span>
          </label>
        </div>
        <button type="button" onClick={add}
          data-testid={`${testIdPrefix}-attrs-add`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          <Plus className="w-3 h-3" /> Add attribute
        </button>
      </div>
      {(attributes || []).length === 0 ? (
        <p className="font-hand text-[11px] text-tb-navy/50">No attributes yet.</p>
      ) : (
        <div className="space-y-2">
          {attributes.map((a, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center"
                 data-testid={`${testIdPrefix}-attr-row-${i}`}>
              <input value={a.label || ""} placeholder="Label (e.g. Passenger Capacity)"
                onChange={(e) => setLabel(i, e.target.value)}
                data-testid={`${testIdPrefix}-attr-label-${i}`}
                className={`${labelCls} h-9 px-2 rounded border border-tb-navy/25 font-hand text-xs text-tb-navy`} />
              <select value={a.type || "string"}
                onChange={(e) => setAt(i, { type: e.target.value })}
                data-testid={`${testIdPrefix}-attr-type-${i}`}
                className={`${typeCls} h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-xs text-tb-navy`}>
                {ATTR_TYPES.map((t) => (
                  <option key={t} value={t}>{ATTR_TYPE_LABELS[t] || t}</option>
                ))}
              </select>
              {advanced && (
                <>
                  <input value={a.key || ""} placeholder="key"
                    onChange={(e) => setKey(i, e.target.value)}
                    data-testid={`${testIdPrefix}-attr-key-${i}`}
                    className="col-span-3 h-9 px-2 rounded border border-tb-navy/25 font-hand text-xs text-tb-navy" />
                  <input value={(a.examples || []).join(", ")}
                    onChange={(e) => setAt(i, {
                      examples: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                    })}
                    placeholder="examples (comma-sep)"
                    data-testid={`${testIdPrefix}-attr-examples-${i}`}
                    className="col-span-2 h-9 px-2 rounded border border-tb-navy/25 font-hand text-xs text-tb-navy" />
                </>
              )}
              <button type="button" onClick={() => remove(i)}
                data-testid={`${testIdPrefix}-attr-remove-${i}`}
                className="col-span-1 h-9 inline-flex items-center justify-center rounded border border-red-300 text-red-700 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Recursive folder editor — depth-limited. Uses React.createElement
// (not JSX) for the recursive `FoldersEditor` and `FolderRow` calls to
// break the visual-edits babel plugin's JSXElement binding-traversal,
// which otherwise infinitely recurses on mutually-referencing components
// and crashes the dev build with "Maximum call stack size exceeded".
function FoldersEditor({ folders, onChange, depth }) {
  const MAX_DEPTH = 4;
  const [openIds, setOpenIds] = useState(() => new Set());

  const patchFolder = (idx, patch) => {
    const next = folders.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const setChildren = (idx, subFolders) => patchFolder(idx, { folders: subFolders });
  const setAttrs = (idx, attrs) => patchFolder(idx, { attributes: attrs });
  const remove = (idx) => {
    const next = folders.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const add = () => onChange([...(folders || []),
    { name: "New folder", entity_type: "other", attributes: [], folders: [] }]);
  const toggle = (idx) => setOpenIds((prev) => {
    const key = String(idx);
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="mt-4 rounded-2xl border border-tb-navy/15 bg-white p-4"
         data-testid={`atlas-template-folders-depth-${depth}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60 flex items-center gap-2">
          <FolderIcon className="w-3 h-3" />
          {depth === 0 ? "Top-level folders" : `Sub-folders (depth ${depth})`}
        </div>
        <button type="button" onClick={add}
          data-testid={`atlas-template-add-folder-depth-${depth}`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-tb-navy/25 text-tb-navy font-hand text-xs hover:bg-tb-navy/5">
          <Plus className="w-3 h-3" /> Add folder
        </button>
      </div>
      {(folders || []).length === 0 ? (
        <p className="font-hand text-[11px] text-tb-navy/50">No folders yet.</p>
      ) : (
        <div className="space-y-2">
          {folders.map((f, i) => React.createElement(FolderRow, {
            key: i, folder: f, index: i, depth,
            open: openIds.has(String(i)),
            onToggle: () => toggle(i),
            onPatch: (patch) => patchFolder(i, patch),
            onSetChildren: (sub) => setChildren(i, sub),
            onSetAttrs: (a) => setAttrs(i, a),
            onRemove: () => remove(i),
            maxDepth: MAX_DEPTH,
          }))}
        </div>
      )}
    </div>
  );
}


function FolderRow({ folder, index, depth, open, onToggle, onPatch, onSetChildren, onSetAttrs, onRemove, maxDepth }) {
  return (
    <div className="border border-tb-navy/10 rounded-xl bg-white"
         data-testid={`atlas-template-folder-${depth}-${index}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={onToggle}
          data-testid={`atlas-template-folder-toggle-${depth}-${index}`}
          className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-tb-navy/5 text-tb-navy/60">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <input value={folder.name || ""} placeholder="Folder name"
          onChange={(e) => onPatch({ name: e.target.value })}
          data-testid={`atlas-template-folder-name-${depth}-${index}`}
          className="flex-1 h-9 px-2 rounded border border-tb-navy/25 font-hand text-sm text-tb-navy" />
        <select value={folder.entity_type || "other"}
          onChange={(e) => onPatch({ entity_type: e.target.value })}
          data-testid={`atlas-template-folder-entity-${depth}-${index}`}
          className="h-9 px-2 rounded border border-tb-navy/25 bg-white font-hand text-xs text-tb-navy w-40">
          {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button type="button" onClick={onRemove}
          data-testid={`atlas-template-folder-remove-${depth}-${index}`}
          className="h-9 w-9 inline-flex items-center justify-center rounded border border-red-300 text-red-700 hover:bg-red-50">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && (
        <div className="border-t border-tb-navy/10 p-3 space-y-3 bg-tb-navy/[0.015]">
          <AttributesTable
            attributes={folder.attributes || []}
            onChange={onSetAttrs}
            testIdPrefix={`atlas-template-folder-${depth}-${index}`}
          />
          {depth < maxDepth ? (
            React.createElement(FoldersEditor, {
              folders: folder.folders || [],
              onChange: onSetChildren,
              depth: depth + 1,
            })
          ) : (
            <p className="font-hand text-[10px] text-tb-navy/50">
              Max nesting depth reached ({maxDepth}).
            </p>
          )}
        </div>
      )}
    </div>
  );
}


// ═════════════════════════════════════════════════════════════
// PARENT-MAP PAGE — table of match rules.
// ═════════════════════════════════════════════════════════════
export function AtlasTemplateParentMapPage() {
  const admNav = useTemplatesAdminNav();
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/atlas/admin/template-parent-map"),
      api.get("/atlas/admin/templates"),
    ])
      .then(([mapRes, tplRes]) => {
        setRows(mapRes.data?.mappings || []);
        setTemplates(tplRes.data?.templates || []);
      })
      .catch(() => toast.error("Failed to load parent map."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const onDelete = async (row) => {
    if (!confirm(`Remove mapping ${row.match_type}:${row.match_value} → ${row.template_key}?`)) return;
    try {
      await api.delete(
        `/atlas/admin/template-parent-map/${encodeURIComponent(row.match_value)}?match_type=${row.match_type}`);
      toast.success("Mapping removed.");
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Delete failed.");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl" data-testid="atlas-parent-map-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button type="button" onClick={() => admNav.goList()}
            data-testid="atlas-parent-map-back"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-tb-navy/70 hover:bg-tb-navy/5 font-hand text-xs">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to templates
          </button>
          <h1 className="mt-2 text-tb-navy text-3xl md:text-4xl font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Template parent map
          </h1>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-xl">
            Rules that decide which template a new topic inherits based on
            its parent. Priority: exact slug → entity_type → slug_suffix.
          </p>
        </div>
        <button type="button" onClick={() => setShowAdd(true)}
          data-testid="atlas-parent-map-add"
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
          <Plus className="w-4 h-4" /> Add mapping
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-tb-navy/15 bg-white overflow-hidden"
           data-testid="atlas-parent-map-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-tb-navy/10 font-hand text-[10px] uppercase tracking-widest text-tb-navy/50">
          <div className="col-span-3">Match type</div>
          <div className="col-span-4">Match value</div>
          <div className="col-span-3">Template</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <div className="p-4 font-hand text-tb-navy/50 text-sm">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center font-hand text-tb-navy/50 text-sm">
            No mappings defined.
          </div>
        )}
        {!loading && rows.map((r, i) => (
          <div key={`${r.match_type}:${r.match_value}`}
               data-testid={`atlas-parent-map-row-${r.match_type}-${r.match_value}`}
               className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-tb-navy/5 last:border-b-0 items-center">
            <div className="col-span-3 font-hand text-xs text-tb-navy/70">{r.match_type}</div>
            <div className="col-span-4 font-hand text-xs text-tb-navy"><code>{r.match_value}</code></div>
            <div className="col-span-3 font-hand text-xs text-tb-navy"><b>{r.template_key}</b></div>
            <div className="col-span-2 text-right">
              <button type="button" onClick={() => onDelete(r)}
                data-testid={`atlas-parent-map-delete-${r.match_type}-${r.match_value}`}
                className="inline-flex items-center gap-1 h-7 px-2 rounded border border-red-300 text-red-700 font-hand text-[10px] hover:bg-red-50">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <AddParentMapDialog
        open={showAdd}
        templates={templates}
        onClose={() => setShowAdd(false)}
        onSaved={() => { setShowAdd(false); load(); }}
      />
    </div>
  );
}


function AddParentMapDialog({ open, templates, onClose, onSaved }) {
  const [matchType, setMatchType] = useState("slug");
  const [matchValue, setMatchValue] = useState("");
  const [templateKey, setTemplateKey] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMatchType("slug"); setMatchValue("");
    setTemplateKey((templates || [])[0]?.key || "");
    setSaving(false);
  }, [open, templates]);

  if (!open) return null;

  const save = async () => {
    if (!matchValue.trim() || !templateKey) {
      toast.error("Match value and template are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/atlas/admin/template-parent-map", {
        match_type: matchType,
        match_value: matchValue.trim(),
        template_key: templateKey,
      });
      toast.success("Mapping added.");
      onSaved?.();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-4"
         data-testid="atlas-parent-map-add-dialog"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-tb-navy/10">
          <h2 className="text-tb-navy text-lg font-semibold"
              style={{ fontFamily: '"Playfair Display", serif' }}>
            Add parent-map rule
          </h2>
          <button type="button" onClick={onClose}
            data-testid="atlas-parent-map-close"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
              Match type
            </span>
            <select value={matchType} onChange={(e) => setMatchType(e.target.value)}
              data-testid="atlas-parent-map-match-type"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
              <option value="slug">slug (exact)</option>
              <option value="entity_type">entity_type</option>
              <option value="slug_suffix">slug_suffix</option>
            </select>
          </label>
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
              Match value
            </span>
            <input value={matchValue} onChange={(e) => setMatchValue(e.target.value)}
              placeholder={matchType === "slug_suffix" ? "e.g. resorts" : matchType === "entity_type" ? "e.g. destination" : "e.g. countries"}
              data-testid="atlas-parent-map-match-value"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
          </label>
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">
              Template
            </span>
            <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}
              data-testid="atlas-parent-map-template-key"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 bg-white font-hand text-sm text-tb-navy">
              {(templates || []).map((t) => (
                <option key={t.key} value={t.key}>{t.name || t.key}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-tb-navy/10 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
            data-testid="atlas-parent-map-save-cancel"
            className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving}
            data-testid="atlas-parent-map-save"
            className="inline-flex items-center gap-1.5 h-9 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
